package com.evoapps.evoshift.widget

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * What the home-screen widget shows (and, inside [WatchSnapshot], the watch: SH-99), written by the app whenever the signed-in user's schedule
 * changes ([ShiftWidgetSync]) and read by the widget without network or login. It holds the next
 * two weeks, so the widget stays right as time passes; [ShiftWidgetContent] picks what to show
 * "now".
 */
@Serializable
data class ShiftWidgetSnapshot(
    /** ISO instant the snapshot was made; the widget says how old its data is. */
    val generatedAt: String,
    val userName: String,
    /** The user's published shifts from today, any workplace. */
    val myShifts: List<WidgetShift>,
    /**
     * Everyone's published shifts today and tomorrow at the current workplace ("whole day").
     * Written by apps before SH-200; newer snapshots keep each workplace's team in [workplaces].
     */
    val team: List<WidgetShift> = emptyList(),
    val teamWorkplaceName: String? = null,
    /**
     * SH-200: the person's active workplaces, each with who works there today and tomorrow, so a
     * widget can show the one(s) it was set up for ([WidgetConfig], [ShiftWidgetContent.select]).
     */
    val workplaces: List<WidgetWorkplace> = emptyList(),
    /** SH-200: the workplace an unconfigured widget shows (the app's current one, else the primary). */
    val defaultWorkplaceId: String? = null,
    /** SH-200: the colours of the appearance picked in the app (SH-196); null: the brand's. */
    val colors: WidgetColors? = null
) {
    fun toJson(): String = json.encodeToString(serializer(), this)

    companion object {
        private val json = Json { ignoreUnknownKeys = true }
        fun fromJson(text: String?): ShiftWidgetSnapshot? =
            text?.let { runCatching { json.decodeFromString(serializer(), it) }.getOrNull() }
    }
}

/**
 * One shift as the widget and the watch need it; [start]/[end] are local ISO date-times
 * (overnight-safe). Lives in the domain module because the phone and the Wear OS app (SH-99)
 * exchange it: the same JSON on both ends.
 */
@Serializable
data class WidgetShift(
    val start: String,
    val end: String,
    val role: String? = null,
    val workplaceName: String? = null,
    /** Who works it (team view only). */
    val person: String? = null,
    /** The shift document's id: what the watch's clock in / out asks the phone to clock (SH-99). */
    val id: String? = null,
    /** Clocked in at (local ISO date-time), when clocked in. */
    val clockedIn: String? = null,
    /** Clocked out at (local ISO date-time), when clocked out. */
    val clockedOut: String? = null,
    /** SH-116: on a break since (local ISO date-time), while clocked in. */
    val onBreakSince: String? = null,
    /** SH-200: the workplace (the widget filters by it); null in snapshots from before. */
    val workplaceId: String? = null
) {
    val date: LocalDate get() = startTime.date
    val startTime: LocalDateTime get() = LocalDateTime.parse(start)
    val endTime: LocalDateTime get() = LocalDateTime.parse(end)
    val startLabel: String get() = start.substringAfter('T').take(5)
    val endLabel: String get() = end.substringAfter('T').take(5)
}

/** SH-200: one of the person's workplaces in the snapshot, with its team today and tomorrow. */
@Serializable
data class WidgetWorkplace(
    val id: String,
    val name: String,
    val team: List<WidgetShift> = emptyList()
)

/**
 * SH-200: the widget's colours for the appearance picked in the app (SH-196), as "#AARRGGBB", so
 * the Android and the iOS widget draw the same tested pairs (WidgetPalette in composeApp).
 */
@Serializable
data class WidgetColors(
    /** "NAVY/GOLD" ([com.evoapps.evoshift.domain.model.Appearance.stored]). */
    val appearance: String,
    /** The base: the widget's background. */
    val background: String,
    /** The accent on the base: titles, the next shift's day and countdown, who works now. */
    val accent: String,
    /** Main text. */
    val text: String,
    /** Secondary text (later shifts' details, who works later). */
    val muted: String
)

/**
 * SH-200: what one widget instance shows. Unset ([DEFAULT]): as before SH-200, the person's
 * shifts at every workplace and who works at the default workplace. One workplace: its shifts and,
 * unless [showTeam] is off, who works there. Several or [allWorkplaces]: their shifts, each with
 * its workplace, never who works.
 */
@Serializable
data class WidgetConfig(
    val workplaceIds: List<String> = emptyList(),
    val allWorkplaces: Boolean = false,
    val showTeam: Boolean = true
) {
    val isDefault: Boolean get() = !allWorkplaces && workplaceIds.isEmpty()

    fun toJson(): String = json.encodeToString(serializer(), this)

    companion object {
        val DEFAULT = WidgetConfig()
        private val json = Json { ignoreUnknownKeys = true }

        /** Missing or broken: [DEFAULT]. */
        fun fromJson(text: String?): WidgetConfig =
            text?.let { runCatching { json.decodeFromString(serializer(), it) }.getOrNull() } ?: DEFAULT
    }
}

/**
 * SH-200: the snapshot as one widget shows it ([ShiftWidgetContent.select]): [snapshot] holds
 * only the chosen workplaces' shifts and, when [showTeam], the team of the one workplace.
 */
data class WidgetSelection(
    val snapshot: ShiftWidgetSnapshot,
    val showTeam: Boolean,
    /** Several workplaces: each shift says where it is. */
    val severalWorkplaces: Boolean
)

/** Pure selection logic shared by every widget size (tested in commonTest). */
object ShiftWidgetContent {

    /**
     * SH-200: what a widget set up with [config] shows. Workplaces the person no longer has are
     * dropped; when none is left (or the widget was never set up) it shows what it did before
     * SH-200: every shift and who works at the default workplace.
     */
    fun select(snapshot: ShiftWidgetSnapshot, config: WidgetConfig): WidgetSelection {
        val known = snapshot.workplaces.map { it.id }
        val chosen = if (config.allWorkplaces) known else config.workplaceIds.distinct().filter { it in known }
        return when {
            chosen.isEmpty() -> {
                val default = snapshot.workplaces.firstOrNull { it.id == snapshot.defaultWorkplaceId }
                WidgetSelection(
                    snapshot.copy(
                        team = default?.team ?: snapshot.team,
                        teamWorkplaceName = default?.name ?: snapshot.teamWorkplaceName
                    ),
                    showTeam = config.isDefault || config.showTeam,
                    severalWorkplaces = false
                )
            }
            chosen.size == 1 -> {
                val workplace = snapshot.workplaces.first { it.id == chosen.single() }
                WidgetSelection(
                    snapshot.copy(
                        myShifts = snapshot.myShifts.filter { it.workplaceId == workplace.id },
                        team = if (config.showTeam) workplace.team else emptyList(),
                        teamWorkplaceName = workplace.name
                    ),
                    showTeam = config.showTeam,
                    severalWorkplaces = false
                )
            }
            else -> WidgetSelection(
                snapshot.copy(
                    myShifts = snapshot.myShifts.filter { it.workplaceId in chosen },
                    team = emptyList(),
                    teamWorkplaceName = null
                ),
                showTeam = false,
                severalWorkplaces = true
            )
        }
    }

    /** The user's shifts that have not ended yet, soonest first. */
    fun upcoming(snapshot: ShiftWidgetSnapshot, now: LocalDateTime, limit: Int = 5): List<WidgetShift> =
        snapshot.myShifts.filter { it.endTime > now }.sortedBy { it.start }.take(limit)

    fun next(snapshot: ShiftWidgetSnapshot, now: LocalDateTime): WidgetShift? = upcoming(snapshot, now, 1).firstOrNull()

    fun isOngoing(shift: WidgetShift, now: LocalDateTime): Boolean = shift.startTime <= now && now < shift.endTime

    /**
     * The day the "whole day" view shows: today while anyone still has to work today, otherwise
     * tomorrow (after closing time the useful question is who opens tomorrow).
     */
    fun teamDay(snapshot: ShiftWidgetSnapshot, now: LocalDateTime): LocalDate {
        val today = now.date
        val todayStillRunning = snapshot.team.any { it.date == today && it.endTime > now }
        return if (todayStillRunning || snapshot.team.none { it.date > today }) today
        else snapshot.team.filter { it.date > today }.minOf { it.date }
    }

    fun team(snapshot: ShiftWidgetSnapshot, day: LocalDate): List<WidgetShift> =
        snapshot.team.filter { it.date == day }.sortedWith(compareBy({ it.start }, { it.person }))
}
